import React from 'react'
import {
  SchemaPayload,
  useSchemasQuery,
} from '@shared/hooks/use-schemas-query.hook'
import { JSONSchema7 } from 'json-schema'
import { useThemeStyles } from '@shared/theme/use-theme-styles'
import { useRouter } from '@shared/hooks/use-router'
import { useHandleResourceState } from '@shared/hooks/use-handle-resource-state.hook'
import { Navigate } from 'react-router-dom'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { ErrorsAlert } from '@shared/components/errors-alert.component'
import {
  getFinalResourceConfig,
  getOmittedFields,
  getResourceConfig,
  getResourceRefSchema,
  getResourceSchemaKeyFromRef,
} from '@modules/admin-generic/admin-generic.utils'
import { isEmpty, omit } from 'lodash'
import { AdminGenericCrudSelect } from '@modules/admin-generic/admin-generic-crud-select.component'
import { ProgressButton } from '@shared/components/progress-button.component'
import { FormProvider } from 'react-hook-form'
import {
  AdminResourceType,
  CLIENT_ADMIN,
  ClientAdminResourceType,
  Roles,
} from '@shared/types'
import { LegalBasisNumberField } from '@modules/legal-basis/legal-basis-number.component'
import { Resource } from '@modules/admin-generic/admin-generic.types'
import { getFieldFilteredResults } from '@modules/client-admin/user-form.utils'
import { onboardingConfig } from '@shared/onboarding/config'
import Joyride from 'react-joyride'
import {
  OnboardingType,
  useOnboardingStore,
} from '@shared/onboarding/onboarding.store'
import {
  addUserDialogSteps,
  administrationSteps,
} from '@shared/onboarding/steps'
import ids from '@shared/onboarding/ids'
import { useACL } from '@shared/hooks/use-acl.hook'
import { ACL } from '@shared/components/acl.component'

type Props = {
  resourceName: string
  resourceEndpoint: string
  resourceNameSingular: string
  schema: SchemaPayload
  properties: JSONSchema7
  requiredProperties: string[]
  isUsersAddDialog?: boolean
  isDisabled?: boolean
}

type RouteQuery = {
  resourceName: string
  resourceID?: string
}

export const AdminGenericCrudDialog = ({
  resourceNameOverride,
  isDisabled,
}: {
  resourceNameOverride?: AdminResourceType | ClientAdminResourceType
  isDisabled?: boolean
}) => {
  const {
    query: { resourceName: resourceNameQuery },
  } = useRouter<RouteQuery>()
  const resourceName = resourceNameOverride || resourceNameQuery
  const isUsersAddDialog = resourceName.includes('users')

  const resourceConfig = getResourceConfig(resourceName) ?? ({} as Resource)
  const {
    dto: resourceSchemaKey,
    singular: resourceNameSingular,
    endpoint: resourceEndpoint,
  } = resourceConfig

  const { schema } = useSchemasQuery()
  const resourceSchema =
    schema?.components?.schemas?.[`${resourceSchemaKey}DTO`]
  const resourceSchemaProperties = resourceSchema?.properties ?? {}

  const resourceFinalSchema = omit(
    resourceSchemaProperties,
    getOmittedFields(
      resourceNameSingular,
      resourceConfig,
      resourceSchemaProperties
    )
  ) as JSONSchema7

  return (
    <AdminGenericCrudDialogChild
      schema={schema ?? {}}
      resourceName={resourceName}
      resourceNameSingular={resourceNameSingular}
      resourceEndpoint={resourceEndpoint}
      properties={resourceFinalSchema}
      requiredProperties={resourceSchema?.required ?? []}
      isUsersAddDialog={isUsersAddDialog}
      isDisabled={isDisabled}
    />
  )
}

export const AdminGenericCrudDialogChild = (props: Props) => {
  const classes = useThemeStyles()
  const { navigate, query } = useRouter<RouteQuery>()
  const isResourceEdit = Boolean(query?.resourceID)
  const { isActive, setActive } = useOnboardingStore()

  const {
    formMethods,
    onAddResource,
    mutation: {
      isResourceCreating,
      isResourceCreationSucceed,
      createResourceErrors,
      resetResourceCreationState,
    },
  } = useHandleResourceState({
    resourceEndpoint: props.resourceEndpoint,
    resourceID: query?.resourceID,
  })

  if (isResourceCreationSucceed) {
    return (
      <Navigate
        to={
          props.resourceName.includes(CLIENT_ADMIN)
            ? `/client-admin/${props.resourceName.replace(new RegExp(`^${CLIENT_ADMIN}\\.`), '')}`
            : `/admin/${props.resourceName}`
        }
      />
    )
  }

  return (
    <Dialog fullWidth open={true} maxWidth="md">
      <ACL
        allowedRoles={[
          Roles.Client,
          Roles.ClientEditor,
          Roles.ClientAdministrator,
          Roles.ClientBasic,
        ]}
      >
        <Joyride
          {...onboardingConfig}
          steps={administrationSteps}
          continuous
          run={isActive(OnboardingType.ADMINISTRATION)}
          callback={(data) => {
            if (data.status === 'finished' || data.status === 'skipped') {
              setActive(OnboardingType.ADMINISTRATION, false)
            }
          }}
        />
      </ACL>
      <form noValidate id={'popup'}>
        <FormProvider {...formMethods}>
          <DialogTitle className={classes.dialogTitle}>
            {isResourceEdit ? 'Edytuj' : 'Utwórz nowy'}
          </DialogTitle>
          <DialogContent dividers>
            <ErrorsAlert
              errors={createResourceErrors}
              onClose={resetResourceCreationState}
            />
            {Object.entries(props.properties ?? {}).map(
              ([property, propertySchema]: [string, JSONSchema7]) => {
                if (props.resourceName === AdminResourceType.LEGAL_BASIS) {
                  if (property === 'documentName') {
                    return (
                      <LegalBasisNumberField
                        formMethods={formMethods}
                        property={property}
                        propertySchema={propertySchema}
                      />
                    )
                  }
                }

                const isArraySchema = propertySchema.type === 'array'

                if (
                  propertySchema.$ref ||
                  (isArraySchema && (propertySchema.items as JSONSchema7)?.$ref)
                ) {
                  const $ref = isArraySchema
                    ? (propertySchema.items as JSONSchema7)?.$ref
                    : propertySchema.$ref
                  const propertyResourceSchemaKey =
                    getResourceSchemaKeyFromRef($ref)

                  /**
                   * @todo sync with BE if we can align all DTOs for CRUD with "Crud" suffix
                   */
                  const $refResourceConfig = getFinalResourceConfig(
                    propertyResourceSchemaKey
                  )
                  const $refSchema = getResourceRefSchema(
                    props.schema?.components?.schemas,
                    $refResourceConfig
                  )

                  if (!isEmpty($refResourceConfig)) {
                    const id =
                      property === 'userRoles'
                        ? ids.ADD_USER_TYPE
                        : property === 'companies'
                          ? ids.LOCATIONS
                          : ''

                    return (
                      <div id={id}>
                        <AdminGenericCrudSelect
                          key={property}
                          name={property}
                          multiple={isArraySchema}
                          label={
                            propertySchema?.description ??
                            $refSchema?.description ??
                            property
                          }
                          resourceConfig={$refResourceConfig}
                          isDisabled={
                            props.resourceName === AdminResourceType.GROUPS &&
                            isResourceEdit
                          }
                          {...getFieldFilteredResults(
                            property,
                            props.resourceName
                          )}
                        />
                      </div>
                    )
                  } else {
                    if (process.env.NODE_ENV === 'development') {
                      console.warn(
                        `No resource configuration found for resource "${$ref}"`
                      )
                    }

                    return null
                  }
                } else {
                  return (
                    <TextField
                      {...formMethods.register(property, {
                        required: props.requiredProperties.includes(property),
                      })}
                      required={props.requiredProperties.includes(property)}
                      key={property}
                      label={propertySchema?.description ?? property}
                      fullWidth
                      multiline={(propertySchema?.maxLength ?? 0) > 50}
                      rows={10}
                      margin="normal"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                    />
                  )
                }
              }
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => navigate(-1)}
              color="primary"
              variant="outlined"
            >
              Anuluj
            </Button>
            <ProgressButton
              onClick={onAddResource}
              isLoading={isResourceCreating}
              color="primary"
              variant="contained"
            >
              Zapisz
            </ProgressButton>
          </DialogActions>
        </FormProvider>
      </form>
    </Dialog>
  )
}
